import { ApiError } from '../../shared/api/ApiClient.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { resolveCustomerCredentialSend } from '../../shared/session/resolveCustomerCredentialSend.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';

export function checkoutSessionQrPath(sessionPublicId: string): string {
  return `/customer-checkout/sessions/${encodeURIComponent(sessionPublicId)}/qr`;
}

export function customerPaymentQrPath(paymentId: string): string {
  return `/customer-payments/${encodeURIComponent(paymentId)}/qr`;
}

/** Wave E / G2 — absolute platform ownership QR (never `/api/__platform__/…`). */
export function platformCustomerPaymentQrPath(paymentId: string): string {
  return `/customer-account/payments/${encodeURIComponent(paymentId)}/qr`;
}

export interface FetchCustomerQrImageInput {
  apiBaseUrl: string;
  tenantCode: string;
  path: string;
  /**
   * URL-tenant callsite; server engagement handles tenant membership activation.
   * Empty / omit / null → cookie-first `credentials:'include'` (XOR; never dual-send).
   * When `platformAbsolute` is true, ignored (cookie-first; parity with platformAccountFetch).
   */
  accessToken?: string | null;
  signal?: AbortSignal;
  /**
   * Wave E / G2 — absolute `/api/v1/...` (platform ownership under customerAuthPlatform).
   * When true, `tenantCode` is unused for URL construction (never `__platform__` slug).
   * Also forces Spec A cookie-first: residual memory Bearer is ignored.
   */
  platformAbsolute?: boolean;
}

/**
 * Proxy PNG from enterprise QR storage (Wave 3+). Not JSON envelope — raw image/png.
 *
 * Callers use URL-tenant routing directly; no client-side tenant switch preflight.
 * Never attach foreign JWT A Authorization to tenant-B URL without a completed switch.
 * G2 — Bearer XOR credentials via {@link resolveCustomerCredentialSend}.
 * Platform chrome: pass `platformAbsolute: true` + {@link platformCustomerPaymentQrPath}.
 * Spec A: `platformAbsolute` voids residual memory Bearer (cookie include, no Authorization).
 */
export async function fetchCustomerQrImageBlob(
  input: FetchCustomerQrImageInput
): Promise<Blob> {
  const trimmedBase = input.apiBaseUrl.replace(/\/+$/, '');
  const normalizedPath = input.path.startsWith('/') ? input.path : `/${input.path}`;
  const platformAbsolute = input.platformAbsolute === true;
  const url = platformAbsolute
    ? `${trimmedBase}/api/v1${normalizedPath}`
    : `${trimmedBase}/api/${encodeURIComponent(input.tenantCode)}/v1${normalizedPath}`;

  // Spec A cookie-first (platformAccountFetch twin): under platformAbsolute,
  // ignore residual memory Bearer so credentials:'omit' never drops HttpOnly jar.
  if (platformAbsolute) {
    void input.accessToken;
  }
  const credentialSend = resolveCustomerCredentialSend({
    bearerToken: platformAbsolute ? null : input.accessToken,
  });
  const headers: Record<string, string> = {
    Accept: 'image/png',
  };
  if (credentialSend.authorizationHeader !== undefined) {
    headers.Authorization = credentialSend.authorizationHeader;
  }

  const response = await stormAwareFetch(url, {
    method: 'GET',
    headers,
    credentials: credentialSend.credentials,
    signal: input.signal,
  });

  if (!response.ok) {
    throw new ApiError(
      resolvePublicApiFailureMessage({ status: response.status }),
      response.status,
    );
  }

  return response.blob();
}
